/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { Skeleton } from '@Components/Skeleton';
import { useTicketedEventPurchases } from '@Hooks/useTicketedEventPurchases';
import { Fish } from '@Types/fish';
import { PaymentStatus } from '@Types/purchase';
import { getMemberOrFirstProfile } from '@Utils/fish';
import { formatCurrency } from '@Utils/utils';
import { Ticket } from 'lucide-react';

export interface TicketOrdersProps {
  eventId: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case PaymentStatus.SUCCESS:
      return 'text-green-600';
    case PaymentStatus.FAILURE:
      return 'text-red-600';
    case PaymentStatus.INITIATED:
      return 'text-yellow-600';
    default:
      return 'text-zinc-600';
  }
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getCustomerName = (fish: Fish) => {
  if (!fish) return 'N/A';
  const fishProfile = getMemberOrFirstProfile(fish);

  if (!fishProfile) return 'N/A';

  const firstName = fishProfile?.fName || '';
  const lastName = fishProfile?.lName || '';
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  return fishProfile.email ?? 'N/A';
};

export default function TicketOrders({ eventId }: TicketOrdersProps) {
  const { data: purchases, status } = useTicketedEventPurchases({
    eventId,
  });

  console.log('Purchases:', purchases);

  if (status === 'pending') {
    return (
      <div className="flex flex-col space-y-8">
        <div className="mb-2">
          <Heading level="h3">Ticket Orders</Heading>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!purchases || purchases.length === 0) {
    return (
      <div className="flex flex-col space-y-8">
        <div className="mb-2">
          <Heading level="h3">Ticket Orders</Heading>
        </div>
        <Card>
          <CardContent>
            <div className="py-8 text-center text-sm text-zinc-500">
              No ticket orders found
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-8">
      <div className="mb-2">
        <Heading level="h3">
          Ticket Orders{' '}
          <span className="font-normal text-zinc-400">
            ({purchases.length})
          </span>
        </Heading>
      </div>
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Confirmation #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tickets</TableHead>
                <TableHead>Promo Code</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.map(({ purchase, tickets }) => (
                <TableRow key={purchase.id}>
                  <TableCell className="font-mono text-sm">
                    {purchase.confirmationNumber || purchase.id.slice(0, 8)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {getCustomerName(tickets[0]?.fish)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">
                    {formatDate(purchase.createdAt)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`font-medium ${getStatusColor(purchase.status)}`}
                    >
                      {purchase.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="flex items-center gap-1 font-medium">
                        <Ticket size={12} /> <div>{tickets.length}</div>
                      </span>
                      <span className="text-xs text-zinc-500">
                        {tickets.map((t) => t.ticketType?.name).join(', ')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {purchase.redemptions && purchase.redemptions.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {purchase.redemptions.map((redemption) => (
                          <Badge key={redemption.id} variant="secondary">
                            {redemption.promoCode?.code || '-'}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-zinc-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(purchase.price)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
